<template>
<div class="field-editor">
	<DxForm :form-data="fieldData" label-mode="floating" @fieldDataChanged="fieldDataChanged" ref="editFieldForm">
		<DxSimpleItem>
			<div :class="{ 'field-editor-header': true, 'dirty': isDirty }">Editing <u>{{ fieldData.field }}</u><span v-if="isDirty" style="font-size:0.8em;"> (unsaved)</span></div>
			<div style="padding: 10px;border: 1px solid rgb(204, 204, 204);box-shadow: rgba(0, 0, 0, 0.1) 0 5px 2px;margin-bottom:10px;display:flex;gap:10px;align-items:flex-start;">
				<div><i style="font-size:2em;" :class="'dx-icon-' + fieldTypeMeta(fieldData.fieldtype).icon"></i></div>
				<div style="color:#333;">{{ fieldTypeMeta(fieldData.fieldtype).hint }}</div>
			</div>
		</DxSimpleItem>
		<DxSimpleItem css-class="disable-form-paddings" data-field="field" :label="{ text: 'Identifier' }" :disabled="true" help-text="Unique identifier for this field." />
		<DxSimpleItem data-field="label" :is-required="true" help-text="Display name for this field." />
		<DxSimpleItem css-class="disable-form-paddings">
			<DxDropDownButton :items="fieldExtrasFilteredWithBadge" key-expr="id" display-expr="text" text="Add Features" icon="codeblock" @item-click="toggleFieldExtras" style="margin:2em 0;width:100%;" />
		</DxSimpleItem>
		<DxSimpleItem :visible="shownFieldComponents.includes('default')" css-class="disable-form-paddings">
			<div class="dx-card-title">Default Value</div>
			<div class="dx-card" style="padding:10px;border-left:5px solid var(--dx-color-icon);margin-bottom:1em;">
				<div class="feature-hint">Enter the value to be filled in when creating a new object. Does not apply to existing objects.</div>
				<AdvTextbox label="Default value" v-model="fieldData.default.value" v-if="fieldData.default && 'value' in fieldData.default" />
			</div>
		</DxSimpleItem>
		<DxSimpleItem :visible="shownFieldComponents.includes('restricted')" css-class="disable-form-paddings">
			<div class="dx-card-title">Restricted Values</div>
			<div class="dx-card" style="padding:10px;border-left:5px solid var(--dx-color-icon);margin-bottom:1em;">
				<div class="feature-hint">This field will only be able to be set to the following values:</div>
				<DxTagBox label="Allowed values" label-mode="floating" v-if="fieldData.restricted && 'values' in fieldData.restricted" :items="fieldData.restricted.values" :value="fieldData.restricted.values"
					:accept-custom-value="true" @custom-item-creating="restrictedCustomItemCreating"
					placeholder="Type and press Enter to add" hint="Press Enter to create a custom value"
					:multiline="true" :search-enabled="true" @value-changed="setRestrictedItemValue"
				/>
			</div>
		</DxSimpleItem>
		<DxSimpleItem :visible="shownFieldComponents.includes('showhide')" css-class="disable-form-paddings">
			<div class="dx-card-title">Show & Hide</div>
			<div class="dx-card" style="padding:10px;border-left:5px solid var(--dx-color-icon);margin-bottom:1em;" v-if="fieldData.showhide">
				<div class="feature-hint">This field will be hidden from the user interfaces in the following locations:</div>
				<div class="dx-field checkbox-field-override" style="margin-bottom:0;">
					<div class="dx-field-label">Any creating forms</div>
					<div class="dx-field-value"><DxCheckBox v-model="fieldData.showhide.create" /></div>
				</div>
				<div class="dx-field checkbox-field-override" style="margin-bottom:0;">
					<div class="dx-field-label">Any editing forms</div>
					<div class="dx-field-value"><DxCheckBox v-model="fieldData.showhide.edit" /></div>
				</div>
				<div class="dx-field checkbox-field-override" style="margin-bottom:0;">
					<div class="dx-field-label">Any viewing pages</div>
					<div class="dx-field-value"><DxCheckBox v-model="fieldData.showhide.view" /></div>
				</div>
				<div class="dx-field checkbox-field-override" style="margin-bottom:0;">
					<div class="dx-field-label">Any data grid columns</div>
					<div class="dx-field-value"><DxCheckBox v-model="fieldData.showhide.grid" /></div>
				</div>
			</div>
		</DxSimpleItem>
		<DxSimpleItem :visible="shownFieldComponents.includes('unique')" css-class="disable-form-paddings">
			<div class="dx-card-title">Uniqueness</div>
			<div class="dx-card" style="padding:10px;border-left:5px solid var(--dx-color-icon);margin-bottom:1em;">
				<div class="feature-hint">This field will be required to be unique across all objects.</div>
			</div>
		</DxSimpleItem>
		<DxSimpleItem :visible="shownFieldComponents.includes('autoinc')" css-class="disable-form-paddings">
			<div class="dx-card-title">Auto-increment</div>
			<div class="dx-card" style="padding:10px;border-left:5px solid var(--dx-color-icon);margin-bottom:1em;">
				<div class="feature-hint">This object will be assigned a unique number, starting from 1. <span v-if="fieldData.fieldtype != 'number'">You can define a more specific code if you want; ensure you use the {number} token.</span></div>
				<AdvTextbox label="Code string" v-model="fieldData.autoinc.code" :custom-tokens="{ number: 'Next unique number' }" v-if="fieldData.autoinc && 'code' in fieldData.autoinc && fieldData.fieldtype != 'number'" />
			</div>
		</DxSimpleItem>
		<DxSimpleItem :visible="shownFieldComponents.includes('changes')" css-class="disable-form-paddings">
			<div class="dx-card-title">Changes lock</div>
			<div class="dx-card" style="padding:10px;border-left:5px solid var(--dx-color-icon);margin-bottom:1em" v-if="fieldData.changes">
				<div class="feature-hint">This field will be locked from being changed by users once it is created. It can only be changed by an administrator.</div>
				<div class="dx-field checkbox-field-override" style="margin-bottom:0;">
					<div class="dx-field-label">Also allow changes by group admins</div>
					<div class="dx-field-value"><DxCheckBox v-model="fieldData.changes.allowGroupAdmin" /></div>
				</div>
			</div>
		</DxSimpleItem>
		<DxSimpleItem css-class="disable-form-paddings">
			<div style="display:flex;justify-content:space-between;padding-top:1em;">
				<DxDropDownButton text="Cancel" type="normal" styling-mode="outlined" :split-button="true" width="130px"
					:items="[{ text: 'Delete', icon: 'trash', template: 'danger-item' }]"
					@button-click="$emit('cancel')" @item-click="promptDelete"
				>
					<template #danger-item="{ data }">
						<div style="background-color: var(--dx-color-danger);color:#fff;font-weight:500;text-transform:uppercase;">
							<div v-if="data.icon" class="dx-list-item-icon-container"><i style="color:#fff;" :class="'dx-icon dx-list-item-icon dx-icon-' + data.icon"></i></div>{{ data.text }}
						</div>
					</template>
				</DxDropDownButton>
				<DxButton text="Save Changes" type="default" styling-mode="contained" @click="saveFieldChanges" />
			</div>
		</DxSimpleItem>
	</DxForm>
</div>
</template>

<style lang="scss">
.field-editor .field-editor-header {
	text-align: center;
	font-size: 1.2em;
	font-weight: bold;
	margin: 1em;
	border-bottom: 1px solid #ccc;
	padding-bottom: 0.5em;
	&.dirty {
		color: var(--dx-color-danger);
	}
}
.field-editor .feature-hint {
	margin: 0.5em 0;
	font-style: italic;
	font-size: 12px;
	color: #666;
}
.field-editor .dx-field.checkbox-field-override .dx-field-label {
	flex-grow: 1;
}
.field-editor .dx-field.checkbox-field-override .dx-field-value {
	max-width: 20px;
}
</style>

<script setup>
/* eslint-disable */
import { ref, defineProps, inject, watch, computed, defineEmits, defineExpose } from 'vue';
import { DxForm, DxSimpleItem } from 'devextreme-vue/form';
import { DxButton } from 'devextreme-vue/button';
import { DxTextBox } from 'devextreme-vue/text-box';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxCheckBox } from 'devextreme-vue/check-box';
import { DxTagBox } from 'devextreme-vue/tag-box';
import { DxDropDownButton } from 'devextreme-vue';
import { fieldExtras, fieldTypeMeta } from '../fields.js';
import { alert as dxAlert, confirm as dxConfirm } from 'devextreme/ui/dialog';
import AdvTextbox from '../components/adv-textbox.vue';

const $http = inject('$http');
const props = defineProps([
	'field',
]);
const emit = defineEmits(['setBusy', 'edited', 'cancel', 'deleted']);

const fieldData = ref({});				// Form values, cloned from props.
const editFieldForm = ref(null);		// DxForm component instance.
const isDirty = ref(false);				// Whether we have made changes to the selected item, and should warn if closing without saving.
defineExpose({ isDirty });

// Note that we only allow editing of field data using this, not creating new fields.
watch(props, (newProps) => {
	fieldData.value = JSON.parse(JSON.stringify(newProps.field));
}, { immediate: true });

// Generic change handler for any field in our editor - set dirty flag.
function fieldDataChanged(ev) {
	isDirty.value = true;
}

// Handle setting custom entries for restricted values TagBox
const ignoreSetRestrictedItemValue = ref(false);
function setRestrictedItemValue(e) {
	if (ignoreSetRestrictedItemValue.value) {
		console.log('ignoreSetRestrictedItemValue due to unwanted call', e);
		ignoreSetRestrictedItemValue.value = false;
		return;
	}
	console.log('setRestrictedItemValue', e, JSON.stringify(fieldData.value));
	fieldData.value.restricted.values = e.value;
	isDirty.value = true;
}

// Handle custom entries for restricted values TagBox
function restrictedCustomItemCreating(e) {
	const rawText = e && typeof e.text === 'string' ? e.text : '';
	const text = rawText.trim();
	if (!text) {
		e.customItem = null;
		return;
	}
	if (!fieldData.value.restricted) {
		fieldData.value.restricted = { enabled: true, values: [] };
	}
	if (!Array.isArray(fieldData.value.restricted.values)) {
		fieldData.value.restricted.values = [];
	}
	if (!fieldData.value.restricted.values.includes(text)) {
		fieldData.value.restricted.values.push(text);
	}
	console.log('restrictedCustomItemCreating', e, JSON.stringify(fieldData.value));
	ignoreSetRestrictedItemValue.value = true;
	e.customItem = text;
	isDirty.value = true;
}

// Field components that can be shown for a single field type in our editor, with badges for active items.
const fieldExtrasFilteredWithBadge = computed(() => {
	const selections = fieldData.value;
	if (!selections)
		return [];
	return fieldExtras.filter(e => !e.restrict || e.restrict.length == 0 || e.restrict.includes(selections.fieldtype))
		.map(e => ({
			...e,
			badge: selections[e.id] ? "✓" : "",
		}));
});

// Field components that are active for the current field, computed - with setter to update the fieldData object.
const shownFieldComponents = computed({
	get: () => {
		if (!fieldData.value)
			return [];
		const meta = fieldTypeMeta(fieldData.value.fieldtype);
		if (!meta.id || meta.internal)
			return [];
		return fieldExtras.filter(e => fieldData.value[e.id]).map(e => e.id);
	},
	set: (newComponents) => {
		if (!fieldData.value)
			return;
		fieldExtras.forEach(extra => {
			// If component is now included but wasn't before, set it to a default value (object with enabled sentinel value). Otherwise, delete it from the value entirely.
			if (newComponents.includes(extra.id) && !fieldData.value[extra.id]) {
				fieldData.value[extra.id] = { enabled: true };
				if (extra.properties && typeof extra.properties === 'object') {
					Object.entries(extra.properties).forEach(([key, value]) => {
						fieldData.value[extra.id][key] = typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value;		// Clone value if it's an object.
					});
				}
			} else if (!newComponents.includes(extra.id) && fieldData.value[extra.id]) {
				delete fieldData.value[extra.id];
			}
		});
		isDirty.value = true;
	}
});

// Toggle a field component on or off by setting the computed value.
const toggleFieldExtras = (e) => {
	let components = shownFieldComponents.value;
	if (!components.includes(e.itemData.id)) {
		components.push(e.itemData.id);
		shownFieldComponents.value = components;
	} else {
		components = components.filter(c => c != e.itemData.id);
		shownFieldComponents.value = components;
	}
};

// Save handler for editing field: validate, then send a PATCH request.
const saveFieldChanges = async () => {
	if (!fieldData.value || !fieldData.value.fieldtype || fieldData.value.internal)
		return;
	const validationResult = await editFieldForm.value.instance.validate();
	if (!validationResult.isValid) {
		return;
	}

	// This is a POST request so we send the entire object always, since spec needs to be fully replaced.
	const postData = {
		label: fieldData.value.label,
		spec: {},
	};
	fieldExtras.forEach(extra => {
		if (fieldData.value[extra.id]) {
			postData.spec[extra.id] = JSON.parse(JSON.stringify(fieldData.value[extra.id]));
		}
	});
	// If field is a relationship, ensure the relation link in the spec is also always preserved.
	if (fieldData.value.fieldtype == 'relationship') {
		postData.spec['relation'] = fieldData.value.relation;
	}
	// TODO: Handle other fields that are put in spec (e.g. date specific fields, etc.)

	emit('setBusy', true);
	$http.put(`/field/${fieldData.value.id}`, postData).then((result) => {
		emit('setBusy', false);
		emit('edited', result.data)
	}).catch(error => {
		console.error(error);
		emit('setBusy', false);
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">${error.response?.data?.message || 'Failed to edit field'}</div></div>`, `Error ${error.response?.status || '0'} / ${error.response?.statusText || 'Unknown'}`);
	});
}

// Delete handler for editable field: prompt for confirmation, validate we are allowed to delete, then send a DELETE request.
const promptDelete = async () => {
	// TODO: Check for existing data in this field that is stored (or references in other fields) - and use item-delete instead.
	const label = fieldData.value && fieldData.value.label ? fieldData.value.label : '';
	const keyspec = fieldData.value && fieldData.value.keyspec ? fieldData.value.keyspec : '';
	const sanitizedLabel = (label || '?').replace(/[<>]/g, '').replace(/"/g, '&quot;');
	const sanitizedKeyspec = (keyspec || '?').replace(/[<>]/g, '').replace(/"/g, '&quot;');
	
	// For relationship fields, we prompt for confirmation that we will be deleting many other fields as well as the base relationship schema as well.
	// Otherwise, the confirmation is just for the field itself. TODO: In the future, we want to check if this field has been actually used or not, and if it is mentioned anywhere.
	const isRelationship = fieldData.value && fieldData.value.fieldtype == 'relationship';
	const confirmMessage = !isRelationship ?
		`Are you sure you want to delete the field <u>${sanitizedKeyspec}</u> <i>(${sanitizedLabel})</i>?<br><b>All data stored in this field will be permanently lost.</b><br><i>Advanced handling and hinting is not yet implemented.</i>` :
		`Are you sure you want to delete the relationship <u>${sanitizedKeyspec}</u> <i>(${sanitizedLabel})</i>?<br><b>This field, and the opposing field, as well as all data stored in either will be permanently lost.</b><br><i>Advanced handling and hinting is not yet implemented.</i>`;
	const confirm = await dxConfirm(confirmMessage, isRelationship ? 'Confirm relationship deletion' : 'Confirm field deletion');
	if (!confirm)
		return;

	emit('setBusy', true);
	$http.delete(`/field/${fieldData.value.id}`).then((result) => {
		emit('setBusy', false);
		emit('deleted', result.data);
	}).catch(error => {
		console.error(error);
		emit('setBusy', false);
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">${error.response?.data?.message || 'Failed to delete field'}</div></div>`, `Error ${error.response?.status || '0'} / ${error.response?.statusText || 'Unknown'}`);
	});
}

</script>
